import { ArrowLeftRight, Palette, X } from "lucide-react";

import type { Pro014StepFourItem } from "@/@types/campaign";
import type { CampaignConflictItem, ConflictingCampaign } from "@/@types/campaign-conflict";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  GRADE_BUTTON_ARIA,
  GRADE_BUTTON_LABEL,
  GRADE_BUTTON_TITLE,
  gradeCountLabel,
} from "../../../components/grade/grade-messages";
import { describeConflictingCampaign } from "../../../utils/conflict-scope";
import {
  hasNonRemovableConflict,
  hasRemovableConflict,
} from "../../../utils/conflict-resolution";
import { hasGrade } from "../../../utils/grade-targets";
import { PRO014_PRODUCTS_TEXTS, PRO014_WIZARD_TEXTS } from "../../constants";

const ROW_BASE_CLASS =
  "flex flex-wrap items-start justify-between gap-2 rounded border p-2 transition-colors";
const ROW_CONFLICT_CLASS = "bg-red-50 border-red-300 hover:border-red-500";
const ROW_CLEAN_CLASS = "bg-white hover:border-primary/50";

interface Pro014ProductRowProps {
  item: Pro014StepFourItem;
  position: number;
  conflict?: CampaignConflictItem;
  gradeRestricted: boolean;
  isReadOnly: boolean;
  onEditGrade: (item: Pro014StepFourItem) => void;
  onRemove: (sku: string) => void;
  onRemoveFromPrevious?: (conflict: CampaignConflictItem) => void;
}

export function Pro014ProductRow({
  item,
  position,
  conflict,
  gradeRestricted,
  isReadOnly,
  onEditGrade,
  onRemove,
  onRemoveFromPrevious,
}: Pro014ProductRowProps): JSX.Element {
  const removable: boolean = conflict ? hasRemovableConflict(conflict) : false;
  const onlyNonRemovable: boolean = conflict
    ? !removable && hasNonRemovableConflict(conflict)
    : false;
  const rowClass: string = conflict ? ROW_CONFLICT_CLASS : ROW_CLEAN_CLASS;
  const removeTitle: string = onlyNonRemovable
    ? PRO014_PRODUCTS_TEXTS.NON_REMOVABLE_TITLE
    : PRO014_PRODUCTS_TEXTS.REMOVE_FROM_PREVIOUS_TITLE;

  return (
    <div className={`${ROW_BASE_CLASS} ${rowClass}`}>
      <div className="flex min-w-0 items-start gap-2">
        <Badge variant="outline" className="mt-0.5 font-mono">
          {PRO014_WIZARD_TEXTS.ITEM_POSITION(position)}
        </Badge>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium">{item.sku}</span>
            {item.name && (
              <span className="break-words text-sm text-muted-foreground">
                {PRO014_WIZARD_TEXTS.ITEM_NAME(item.name)}
              </span>
            )}
            {gradeRestricted && hasGrade(item) && (
              <Badge variant="secondary" className="gap-1 text-[10px] font-normal">
                <Palette className="h-3 w-3" aria-hidden />
                {gradeCountLabel(item.grades)}
              </Badge>
            )}
          </div>
          {conflict && (
            <div className="mt-1 flex flex-wrap gap-1">
              {conflict.conflictingCampaigns.map((campaign: ConflictingCampaign, index: number) => (
                <Badge
                  key={`${campaign.code}-${campaign.storeCode}-${campaign.listNumber ?? ""}-${index}`}
                  variant="destructive"
                  className="max-w-full whitespace-normal break-words text-[10px] font-normal"
                >
                  {describeConflictingCampaign(campaign)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center gap-1">
        {conflict && onRemoveFromPrevious && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1"
            disabled={isReadOnly || !removable}
            title={removeTitle}
            onClick={() => onRemoveFromPrevious(conflict)}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
            {PRO014_PRODUCTS_TEXTS.REMOVE_FROM_PREVIOUS}
          </Button>
        )}
        {gradeRestricted && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1"
            disabled={isReadOnly}
            title={GRADE_BUTTON_TITLE}
            aria-label={GRADE_BUTTON_ARIA(item.sku)}
            onClick={() => onEditGrade(item)}
          >
            <Palette className="h-3.5 w-3.5" aria-hidden />
            {GRADE_BUTTON_LABEL}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onRemove(item.sku)}
          disabled={isReadOnly}
          title={PRO014_PRODUCTS_TEXTS.DISCARD_ITEM_TITLE}
          aria-label={PRO014_PRODUCTS_TEXTS.DISCARD_ITEM_ARIA(item.sku)}
          className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
